import css from './test.module.scss';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useHistoryModal } from 'com/HistoryModal';
import { useQuestionEditor } from 'com/QuestionEditor';
import { apiDeleteQuestion, apiSubmitQuestion } from 'core/api';
import { doubleConfirm } from 'core/helpers/browser';
import { useStableCallback } from 'core/hooks/useStableCallback';
import { getDashboardUrl, getTestQuestionsUrl } from 'core/paths';
import { FrameHeader, FrameMessage, FramePreloader } from 'core/ui/Frame';
import { MenuItem } from 'core/ui/Menu';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS, { Icon } from 'core/ui/icons';

import Footer from './Footer';
import Question from './Question';
import { TAuxQuestion } from './types';
import { useFetchData } from './useFetchData';

export function Test() {
  const { showError } = useMessageModal();
  const { showQuestionHistory } = useHistoryModal();
  const { test, questions, updateQuestion, deleteQuestion, error, isLoading } =
    useFetchData();

  const [pos, setPos] = useState(0);
  const [wrongAttempts, setWrong] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number | undefined>>(
    {}
  );

  const question = questions[pos];
  const selection = answers[pos];

  const onSelect = useStableCallback((idx: number): void => {
    setAnswers({ ...answers, [pos]: idx });
    const isCorrect = question.rightOption === idx;

    if (!isCorrect) {
      setWrong(wrongAttempts + 1);
    } else if (pos + 1 < questions.length) {
      setTimeout(() => {
        setPos(pos + 1);
      }, SUCCESS_DELAY);
    }

    if (isDue(question)) {
      apiSubmitQuestion(question.uid, isCorrect)
        .then(updateQuestion)
        .catch(showError);
    }
  });

  function onDelete(question: TQuestion) {
    if (doubleConfirm()) {
      apiDeleteQuestion(question.uid)
        .then(() => {
          setAnswers({ ...answers, [pos]: undefined });
          setPos(prev => Math.min(prev, questions.length - 2));
          deleteQuestion(question.uid);
        })
        .catch(showError);
    }
  }

  const navigate = useNavigate();

  const { editQuestion } = useQuestionEditor();
  const onEdit = () => {
    editQuestion(question.uid)
      .then(updated => {
        if (!updated) return;
        updateQuestion(updated);
        setAnswers({ ...answers, [pos]: undefined });
      })
      .catch(showError);
  };

  return (
    <div className={css.root}>
      <FrameHeader
        closeLink={getDashboardUrl()}
        menuItems={
          test ? (
            <>
              <MenuItem
                label="Edit question"
                onClick={onEdit}
                icon={ICONS.Edit}
                kkb="F2"
              />
              <MenuItem
                label="Delete question"
                onClick={() => onDelete(question)}
                icon={ICONS.Trash}
              />
              <MenuItem
                label="Show status"
                onClick={() => showQuestionHistory(question.uid)}
                icon={ICONS.Graph}
                kkb="F3"
              />
              <MenuItem
                label="Show all questions"
                onClick={() => navigate(getTestQuestionsUrl(orDie(test).uid))}
                icon={ICONS.Cards}
              />
            </>
          ) : undefined
        }
      >
        <Icon icon="Question" className={css.questionIcon} />
        Test: <span className={css.testName}>{test?.name ?? 'loading…'}</span>
      </FrameHeader>

      {isLoading && <FramePreloader />}

      {test && !questions.length && <FrameMessage message="no questions" />}

      {error && <FrameMessage message={`Something went wrong.`} />}

      {question && (
        <Question
          question={question}
          selection={selection ?? null}
          onSelect={onSelect}
          isDue={isDue(question)}
        />
      )}

      {questions.length > 0 && (
        <Footer
          pos={pos}
          questionCount={questions.length}
          setPos={setPos}
          wrongAttempts={wrongAttempts}
        />
      )}
    </div>
  );
}

const SUCCESS_DELAY = 500; // ms;

const isDue = (question: TAuxQuestion): boolean =>
  new Date(question.nextProbeAt) < new Date();
